import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ArrowRight, ArrowLeft, SkipForward, Loader2, Sparkles, GitPullRequest, AlertCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/lib/i18n";
import LangSwitcher from "@/components/LangSwitcher";
import { ProviderLogo } from "@/components/ProviderLogo";

const BOARDS = [
  { type: "azure_devops",  fields: [["base_url", "https://dev.azure.com/yourorg"], ["project", "Support"], ["pat", "password"]] },
  { type: "github_issues", fields: [["owner", "acme"], ["repo", "support"], ["token", "password"]] },
  { type: "gitlab_issues", fields: [["base_url", "https://gitlab.com"], ["project_id", "12345"], ["token", "password"]] },
  { type: "jira",          fields: [["base_url", "https://acme.atlassian.net"], ["project_key", "SUP"], ["email", "you@acme.com"], ["token", "password"]] },
  { type: "linear",        fields: [["team_id", "TEAM"], ["api_key", "password"]] },
  { type: "servicenow",    fields: [["instance_url", "https://acme.service-now.com"], ["user", "admin"], ["password", "password"]] },
  { type: "zendesk",       fields: [["subdomain", "acme"], ["email", "you@acme.com"], ["token", "password"]] },
  { type: "freshdesk",     fields: [["domain", "acme.freshdesk.com"], ["api_key", "password"]] },
  { type: "pagerduty",     fields: [["api_key", "password"]] },
];

const LOGS = [
  { type: "aws",          fields: [["region", "us-east-1"], ["access_key_id", "AKIA..."], ["secret_access_key", "password"]] },
  { type: "azure_monitor",fields: [["tenant_id", ""], ["client_id", ""], ["client_secret", "password"], ["workspace_id", ""]] },
  { type: "gcp_logging",  fields: [["project_id", "my-gcp-project"], ["service_account_json", "password"]] },
  { type: "datadog",      fields: [["site", "datadoghq.com"], ["api_key", "password"], ["app_key", "password"]] },
  { type: "newrelic",     fields: [["account_id", ""], ["api_key", "password"]] },
  { type: "splunk",       fields: [["host", ""], ["port", "8089"], ["token", "password"]] },
  { type: "grafana_loki", fields: [["url", "https://loki..."], ["user", ""], ["password", "password"]] },
  { type: "opensearch",   fields: [["host", ""], ["port", "9200"], ["user", ""], ["password", "password"]] },
  { type: "sentry",       fields: [["org_slug", "acme"], ["auth_token", "password"]] },
];

const REPOS = [
  { type: "github_repo",       fields: [["owner", "acme"], ["repo", "monorepo"], ["token", "password"]] },
  { type: "gitlab_repo",       fields: [["base_url", "https://gitlab.com"], ["project_id", "12345"], ["token", "password"]] },
  { type: "bitbucket_repo",    fields: [["workspace", "acme"], ["repo_slug", "core"], ["user", ""], ["app_password", "password"]] },
  { type: "azure_devops_repo", fields: [["base_url", "https://dev.azure.com/acme"], ["project", "Core"], ["repo", "main-repo"], ["pat", "password"]] },
];

const DATABASES = [
  { type: "postgres",      fields: [["host", "db.example.com"], ["port", "5432"], ["database", "mydb"], ["user", ""], ["password", "password"]] },
  { type: "mysql",         fields: [["host", "db.example.com"], ["port", "3306"], ["database", "mydb"], ["user", ""], ["password", "password"]] },
  { type: "mssql",         fields: [["host", "db.example.com"], ["port", "1433"], ["database", "mydb"], ["user", ""], ["password", "password"]] },
  { type: "mongodb",       fields: [["host", "cluster.mongodb.net"], ["port", "27017"], ["database", "mydb"], ["user", ""], ["password", "password"]] },
  { type: "redis",         fields: [["host", "redis.example.com"], ["port", "6379"], ["password", "password"]] },
  { type: "elasticsearch", fields: [["host", "es.example.com"], ["port", "9200"], ["user", ""], ["password", "password"]] },
];

const STR = (lang) => ({
  step: lang === "pt" ? "Passo" : "Step",
  of: lang === "pt" ? "de" : "of",
  skip: lang === "pt" ? "Pular" : "Skip",
  back: lang === "pt" ? "Voltar" : "Back",
  next: lang === "pt" ? "Continuar" : "Continue",
  connect: lang === "pt" ? "Conectar" : "Connect",
  finish: lang === "pt" ? "Concluir" : "Finish",
  s1Title: lang === "pt" ? "Conecte seu board" : "Connect your board",
  s1Desc: lang === "pt" ? "Onde ficam os cards/tickets que o Ratchet vai analisar? Pode conectar mais de um depois." : "Where do the cards/tickets live? You can connect more later.",
  s2Title: lang === "pt" ? "Logs & Observability" : "Logs & Observability",
  s2Desc: lang === "pt" ? "De onde a IA vai puxar logs para investigar a causa raiz." : "Where the AI pulls logs from to investigate root cause.",
  s3Title: lang === "pt" ? "Contexto da empresa" : "Company context",
  s3Desc: lang === "pt" ? "Quanto mais contexto, mais preciso o relatório. Cole a URL do site e a IA preenche, ou escreva manualmente." : "More context = sharper report. Drop the website URL and AI fills it, or write manually.",
  s3GenerateBtn: lang === "pt" ? "Gerar com IA" : "Generate with AI",
  s3Url: lang === "pt" ? "URL do site" : "Website URL",
  s3Name: lang === "pt" ? "Nome da empresa" : "Company name",
  s3Industry: lang === "pt" ? "Ramo / Indústria" : "Industry",
  s3Description: lang === "pt" ? "Descrição (o que faz?)" : "Description",
  s3Stack: lang === "pt" ? "Stack provável (separe por vírgula)" : "Likely stack (comma-separated)",
  s3Critical: lang === "pt" ? "Sistemas críticos (separe por vírgula)" : "Critical systems (comma-separated)",
  s3Products: lang === "pt" ? "Produtos públicos (separe por vírgula)" : "Public products (comma-separated)",
  s4Title: lang === "pt" ? "Repositórios de código" : "Code repositories",
  s4Desc: lang === "pt" ? "Clonagem isolada por sessão. O Ratchet roda fora do core do cliente — sem cross-contamination. Configure auto-PR se quiser." : "Per-session isolated clone. Ratchet runs outside your core — no cross-contamination. Set up auto-PR if you want.",
  s4PrTarget: lang === "pt" ? "Branch de destino dos PRs" : "PR target branch",
  s4PrAuto: lang === "pt" ? "Criar PR automaticamente quando a IA resolver" : "Auto-create PR when AI resolves",
  s4PrTitle: lang === "pt" ? "Template do título do PR" : "PR title template",
  s4PrReviewers: lang === "pt" ? "Revisores (separados por vírgula)" : "Reviewers (comma-separated)",
  s4Isolation: lang === "pt" ? "Isolamento: cada análise roda em sessão própria com subprocess agents (extractor, analyzer, root-cause, PR-drafter) — cada um com contexto focado e token-budget cap. Sem prompts monolíticos, sem cross-tenant leak." : "Isolation: each analysis runs in its own session with subprocess agents (extractor, analyzer, root-cause, PR-drafter) — each with focused context and a token-budget cap. No monolithic prompts, no cross-tenant leak.",
  s5Title: lang === "pt" ? "Tudo pronto" : "All set",
  s5Desc: lang === "pt" ? "Comece a analisar cards no dashboard." : "Start analyzing cards on the dashboard.",
  s5Open: lang === "pt" ? "Abrir Dashboard" : "Open Dashboard",
  sDbTitle: lang === "pt" ? "Banco de dados (opcional, recomendado)" : "Database (optional, recommended)",
  sDbDesc: lang === "pt"
    ? "Conectar seu banco aumenta significativamente a acurácia da investigação — o Ratchet introspecta o schema e usa os nomes reais (tabelas, colunas, coleções) ao gerar queries. Pular agora resulta em modo 'schema parsing only' (heurísticas em ORM)."
    : "Connecting your database significantly increases investigation accuracy — Ratchet introspects the schema and uses real names (tables, columns, collections) when generating queries. Skipping now results in 'schema parsing only' mode (ORM heuristics).",
});

export default function Onboarding() {
  const { lang } = useLang();
  const t = STR(lang);
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [step, setStep] = useState(0);

  // step 1: boards (multi)
  const [boardSel, setBoardSel] = useState("azure_devops");
  const [boardForm, setBoardForm] = useState({});
  const [savedBoards, setSavedBoards] = useState([]);

  // step 2: logs (multi)
  const [logSel, setLogSel] = useState("aws");
  const [logForm, setLogForm] = useState({});
  const [savedLogs, setSavedLogs] = useState([]);

  // step 3: company context
  const [ctxUrl, setCtxUrl] = useState("");
  const [ctxLoading, setCtxLoading] = useState(false);
  const [ctxWarn, setCtxWarn] = useState("");
  const [ctx, setCtx] = useState({
    name: "", industry: "", description: "",
    likely_stack: [], critical_systems: [], public_products: [], source_url: "",
  });

  // step 3: database (multi) — NEW
  const [dbSel, setDbSel] = useState("postgres");
  const [dbForm, setDbForm] = useState({});
  const [savedDbs, setSavedDbs] = useState([]);

  // step 4: repos (multi)
  const [repoSel, setRepoSel] = useState("github_repo");
  const [repoForm, setRepoForm] = useState({
    pr_target_branch: "main",
    pr_auto_create: "false",
    pr_title_template: "fix(ratchet): {card_title}",
    pr_reviewers: "",
  });
  const [savedRepos, setSavedRepos] = useState([]);

  const STEPS_COUNT = 6;
  const meta = (type, list) => list.find((x) => x.type === type);

  const saveBoard = async () => {
    const def = meta(boardSel, BOARDS);
    const config = {}, credentials = {};
    for (const [field, ph] of def.fields) {
      if (ph === "password") credentials[field] = boardForm[field] || "";
      else config[field] = boardForm[field] || "";
    }
    const has_cred = Object.values(credentials).some(Boolean);
    if (!has_cred) return setStep(1);
    await api.post("/integrations", { type: boardSel, name: def.type, config, credentials });
    setSavedBoards([...savedBoards, boardSel]);
    setBoardForm({});
  };

  const saveLog = async () => {
    const def = meta(logSel, LOGS);
    const config = {}, credentials = {};
    for (const [field, ph] of def.fields) {
      if (ph === "password") credentials[field] = logForm[field] || "";
      else config[field] = logForm[field] || "";
    }
    const has_cred = Object.values(credentials).some(Boolean);
    if (!has_cred) return setStep(2);
    await api.post("/integrations", { type: logSel, name: def.type, config, credentials });
    setSavedLogs([...savedLogs, logSel]);
    setLogForm({});
  };

  const generateCtx = async () => {
    if (!ctxUrl) return;
    setCtxLoading(true);
    setCtxWarn("");
    try {
      const { data } = await api.post("/onboarding/company-from-url", { url: ctxUrl });
      setCtx({
        name: data.name || "",
        industry: data.industry || "",
        description: data.description || "",
        likely_stack: data.likely_stack || [],
        critical_systems: data.critical_systems || [],
        public_products: data.public_products || [],
        source_url: data.source_url || ctxUrl,
      });
      if (data._warning) setCtxWarn(data._warning);
    } catch (e) {
      setCtxWarn(e?.response?.data?.detail || "Generation failed");
    } finally {
      setCtxLoading(false);
    }
  };

  const saveCtx = async () => {
    const hasContent = ctx.name || ctx.industry || ctx.description;
    if (hasContent) {
      await api.put("/orgs/current/company-context", ctx);
    }
    setStep(3);
  };

  const saveDb = async () => {
    const def = meta(dbSel, DATABASES);
    const config = {}, credentials = {};
    for (const [field, ph] of def.fields) {
      if (ph === "password") credentials[field] = dbForm[field] || "";
      else config[field] = dbForm[field] || "";
    }
    const has_cred = Object.values(credentials).some(Boolean);
    if (!has_cred) return setStep(4);
    await api.post("/integrations", { type: dbSel, name: def.type, config, credentials });
    setSavedDbs([...savedDbs, dbSel]);
    setDbForm({});
  };

  const saveRepo = async () => {
    const def = meta(repoSel, REPOS);
    const config = {}, credentials = {};
    for (const [field, ph] of def.fields) {
      if (ph === "password") credentials[field] = repoForm[field] || "";
      else config[field] = repoForm[field] || "";
    }
    // PR settings live in config
    config.pr_target_branch = repoForm.pr_target_branch || "main";
    config.pr_auto_create = String(repoForm.pr_auto_create === "true" || repoForm.pr_auto_create === true);
    config.pr_title_template = repoForm.pr_title_template || "fix(ratchet): {card_title}";
    config.pr_reviewers = repoForm.pr_reviewers || "";
    const has_cred = Object.values(credentials).some(Boolean);
    if (!has_cred) return setStep(5);
    await api.post("/integrations", { type: repoSel, name: def.type, config, credentials });
    setSavedRepos([...savedRepos, repoSel]);
    setRepoForm((f) => ({ ...f, owner: "", repo: "", token: "", project_id: "", workspace: "", repo_slug: "", user: "", app_password: "", project: "", pat: "" }));
  };

  const finish = async () => { await refresh(); nav("/app"); };

  const renderFields = (def, formState, setForm) => def.fields.map(([field, ph]) => (
    <div key={field}>
      <label className="overline block mb-1">{field}</label>
      <input
        type={ph === "password" ? "password" : "text"}
        value={formState[field] ?? ""}
        onChange={(e) => setForm({ ...formState, [field]: e.target.value })}
        placeholder={ph === "password" ? "" : ph}
        className="w-full bg-white/5 border border-white/10 px-3 py-2 text-sm font-mono"
        data-testid={`onb-field-${field}`}
      />
    </div>
  ));

  const ProviderGrid = ({ items, selected, onSelect, saved }) => (
    <div className="grid grid-cols-3 md:grid-cols-5 gap-2 mb-6">
      {items.map((it) => {
        const isSel = selected === it.type;
        const isSaved = saved.includes(it.type);
        return (
          <button
            key={it.type}
            onClick={() => onSelect(it.type)}
            data-testid={`onb-provider-${it.type}`}
            className={`relative flex flex-col items-center gap-2 p-3 border transition ${
              isSel ? "border-primary bg-primary/10" : "border-white/10 hover:border-white/30"
            }`}
          >
            <ProviderLogo type={it.type} size={36} />
            <div className="text-[10px] font-mono text-white/60 text-center leading-tight truncate w-full">{it.type}</div>
            {isSaved && (
              <span className="absolute top-1 right-1 w-4 h-4 bg-emerald-400 text-black flex items-center justify-center">
                <Check className="w-3 h-3" strokeWidth={3} />
              </span>
            )}
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6 relative">
      <div className="absolute top-6 right-6"><LangSwitcher /></div>
      <div className="w-full max-w-3xl">
        <div className="flex items-center gap-2 mb-12">
          {Array.from({ length: STEPS_COUNT }).map((_, i) => (
            <div key={i} className={`h-1 flex-1 ${i <= step ? "bg-primary" : "bg-white/10"}`} />
          ))}
        </div>

        <div className="overline mb-3">{t.step} {step + 1} {t.of} {STEPS_COUNT}</div>

        {/* STEP 0 — boards */}
        {step === 0 && (
          <>
            <h1 className="font-display font-black text-4xl tracking-tighter mb-2">{t.s1Title}</h1>
            <p className="text-white/60 mb-8 text-sm">{t.s1Desc}</p>
            <div className="surface p-6">
              <ProviderGrid items={BOARDS} selected={boardSel} onSelect={setBoardSel} saved={savedBoards} />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                {renderFields(meta(boardSel, BOARDS), boardForm, setBoardForm)}
              </div>
              <div className="flex gap-2 flex-wrap">
                <button onClick={saveBoard} className="btn-primary text-sm" data-testid="onb-board-save">{t.connect} <ArrowRight className="w-4 h-4" /></button>
                <button onClick={() => setStep(1)} className="btn-ghost text-sm" data-testid="onb-board-next">{savedBoards.length ? t.next : t.skip} <SkipForward className="w-4 h-4" /></button>
                {savedBoards.length > 0 && <div className="text-xs text-white/40 self-center ml-2 font-mono">// {savedBoards.length} {lang === "pt" ? "conectado(s)" : "connected"}</div>}
              </div>
            </div>
          </>
        )}

        {/* STEP 1 — logs */}
        {step === 1 && (
          <>
            <h1 className="font-display font-black text-4xl tracking-tighter mb-2">{t.s2Title}</h1>
            <p className="text-white/60 mb-8 text-sm">{t.s2Desc}</p>
            <div className="surface p-6">
              <ProviderGrid items={LOGS} selected={logSel} onSelect={setLogSel} saved={savedLogs} />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                {renderFields(meta(logSel, LOGS), logForm, setLogForm)}
              </div>
              <div className="flex gap-2 flex-wrap">
                <button onClick={() => setStep(0)} className="btn-ghost text-sm" data-testid="onb-log-back"><ArrowLeft className="w-4 h-4" /> {t.back}</button>
                <button onClick={saveLog} className="btn-primary text-sm" data-testid="onb-log-save">{t.connect} <ArrowRight className="w-4 h-4" /></button>
                <button onClick={() => setStep(2)} className="btn-ghost text-sm" data-testid="onb-log-next">{savedLogs.length ? t.next : t.skip} <SkipForward className="w-4 h-4" /></button>
                {savedLogs.length > 0 && <div className="text-xs text-white/40 self-center ml-2 font-mono">// {savedLogs.length} {lang === "pt" ? "conectado(s)" : "connected"}</div>}
              </div>
            </div>
          </>
        )}

        {/* STEP 2 — company context */}
        {step === 2 && (
          <>
            <h1 className="font-display font-black text-4xl tracking-tighter mb-2">{t.s3Title}</h1>
            <p className="text-white/60 mb-8 text-sm">{t.s3Desc}</p>
            <div className="surface p-6">
              <div className="flex gap-2 items-end mb-4">
                <div className="flex-1">
                  <label className="overline block mb-1">{t.s3Url}</label>
                  <input value={ctxUrl} onChange={(e) => setCtxUrl(e.target.value)} placeholder="https://acme.com"
                    className="w-full bg-white/5 border border-white/10 px-3 py-2 text-sm font-mono" data-testid="onb-ctx-url" />
                </div>
                <button onClick={generateCtx} disabled={ctxLoading || !ctxUrl} className="btn-primary text-sm whitespace-nowrap" data-testid="onb-ctx-generate">
                  {ctxLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {t.s3GenerateBtn}
                </button>
              </div>
              {ctxWarn && (
                <div className="flex items-start gap-2 bg-amber-500/10 border border-amber-500/30 p-3 mb-4 text-xs text-amber-200 font-mono" data-testid="onb-ctx-warn">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" /> {ctxWarn}
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="overline block mb-1">{t.s3Name}</label>
                  <input value={ctx.name} onChange={(e) => setCtx({ ...ctx, name: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 px-3 py-2 text-sm" data-testid="onb-ctx-name" />
                </div>
                <div>
                  <label className="overline block mb-1">{t.s3Industry}</label>
                  <input value={ctx.industry} onChange={(e) => setCtx({ ...ctx, industry: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 px-3 py-2 text-sm" data-testid="onb-ctx-industry" />
                </div>
                <div className="md:col-span-2">
                  <label className="overline block mb-1">{t.s3Description}</label>
                  <textarea value={ctx.description} onChange={(e) => setCtx({ ...ctx, description: e.target.value })} rows={3}
                    className="w-full bg-white/5 border border-white/10 px-3 py-2 text-sm" data-testid="onb-ctx-desc" />
                </div>
                <div className="md:col-span-2">
                  <label className="overline block mb-1">{t.s3Stack}</label>
                  <input value={(ctx.likely_stack || []).join(", ")} onChange={(e) => setCtx({ ...ctx, likely_stack: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                    className="w-full bg-white/5 border border-white/10 px-3 py-2 text-sm font-mono" data-testid="onb-ctx-stack" />
                </div>
                <div className="md:col-span-2">
                  <label className="overline block mb-1">{t.s3Critical}</label>
                  <input value={(ctx.critical_systems || []).join(", ")} onChange={(e) => setCtx({ ...ctx, critical_systems: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                    className="w-full bg-white/5 border border-white/10 px-3 py-2 text-sm font-mono" data-testid="onb-ctx-critical" />
                </div>
                <div className="md:col-span-2">
                  <label className="overline block mb-1">{t.s3Products}</label>
                  <input value={(ctx.public_products || []).join(", ")} onChange={(e) => setCtx({ ...ctx, public_products: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                    className="w-full bg-white/5 border border-white/10 px-3 py-2 text-sm font-mono" data-testid="onb-ctx-products" />
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <button onClick={() => setStep(1)} className="btn-ghost text-sm" data-testid="onb-ctx-back"><ArrowLeft className="w-4 h-4" /> {t.back}</button>
                <button onClick={saveCtx} className="btn-primary text-sm" data-testid="onb-ctx-save">{t.next} <ArrowRight className="w-4 h-4" /></button>
                <button onClick={() => setStep(3)} className="btn-ghost text-sm" data-testid="onb-ctx-skip">{t.skip} <SkipForward className="w-4 h-4" /></button>
              </div>
            </div>
          </>
        )}

        {/* STEP 3 — database (NEW) */}
        {step === 3 && (
          <>
            <h1 className="font-display font-black text-4xl tracking-tighter mb-2">{t.sDbTitle}</h1>
            <p className="text-white/60 mb-4 text-sm">{t.sDbDesc}</p>
            <div className="surface p-6">
              <ProviderGrid items={DATABASES} selected={dbSel} onSelect={setDbSel} saved={savedDbs} />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                {renderFields(meta(dbSel, DATABASES), dbForm, setDbForm)}
              </div>
              <div className="flex gap-2 flex-wrap">
                <button onClick={() => setStep(2)} className="btn-ghost text-sm" data-testid="onb-db-back"><ArrowLeft className="w-4 h-4" /> {t.back}</button>
                <button onClick={saveDb} className="btn-primary text-sm" data-testid="onb-db-save">{t.connect} <ArrowRight className="w-4 h-4" /></button>
                <button onClick={() => setStep(4)} className="btn-ghost text-sm" data-testid="onb-db-next">{savedDbs.length ? t.next : t.skip} <SkipForward className="w-4 h-4" /></button>
                {savedDbs.length > 0 && <div className="text-xs text-white/40 self-center ml-2 font-mono">// {savedDbs.length} {lang === "pt" ? "conectado(s)" : "connected"}</div>}
              </div>
            </div>
          </>
        )}

        {/* STEP 4 — repos */}
        {step === 4 && (
          <>
            <h1 className="font-display font-black text-4xl tracking-tighter mb-2">{t.s4Title}</h1>
            <p className="text-white/60 mb-4 text-sm">{t.s4Desc}</p>
            <div className="surface p-6">
              <div className="flex items-start gap-2 bg-primary/10 border border-primary/30 p-3 mb-5 text-xs text-primary/90 font-mono" data-testid="onb-repo-isolation-banner">
                <GitPullRequest className="w-4 h-4 shrink-0 mt-0.5" /> {t.s4Isolation}
              </div>
              <ProviderGrid items={REPOS} selected={repoSel} onSelect={setRepoSel} saved={savedRepos} />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                {renderFields(meta(repoSel, REPOS), repoForm, setRepoForm)}
              </div>
              <div className="overline mb-3">// pull request settings</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                <div>
                  <label className="overline block mb-1">{t.s4PrTarget}</label>
                  <input value={repoForm.pr_target_branch} onChange={(e) => setRepoForm({ ...repoForm, pr_target_branch: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 px-3 py-2 text-sm font-mono" data-testid="onb-repo-pr-target" />
                </div>
                <div>
                  <label className="overline block mb-1">{t.s4PrTitle}</label>
                  <input value={repoForm.pr_title_template} onChange={(e) => setRepoForm({ ...repoForm, pr_title_template: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 px-3 py-2 text-sm font-mono" data-testid="onb-repo-pr-title" />
                </div>
                <div className="md:col-span-2">
                  <label className="overline block mb-1">{t.s4PrReviewers}</label>
                  <input value={repoForm.pr_reviewers} onChange={(e) => setRepoForm({ ...repoForm, pr_reviewers: e.target.value })}
                    placeholder="alice, bob, charlie"
                    className="w-full bg-white/5 border border-white/10 px-3 py-2 text-sm font-mono" data-testid="onb-repo-pr-reviewers" />
                </div>
                <label className="md:col-span-2 inline-flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={repoForm.pr_auto_create === "true" || repoForm.pr_auto_create === true}
                    onChange={(e) => setRepoForm({ ...repoForm, pr_auto_create: e.target.checked ? "true" : "false" })}
                    className="w-4 h-4" data-testid="onb-repo-pr-auto" />
                  <span>{t.s4PrAuto}</span>
                </label>
              </div>
              <div className="flex gap-2 flex-wrap">
                <button onClick={() => setStep(3)} className="btn-ghost text-sm" data-testid="onb-repo-back"><ArrowLeft className="w-4 h-4" /> {t.back}</button>
                <button onClick={saveRepo} className="btn-primary text-sm" data-testid="onb-repo-save">{t.connect} <ArrowRight className="w-4 h-4" /></button>
                <button onClick={() => setStep(5)} className="btn-ghost text-sm" data-testid="onb-repo-next">{savedRepos.length ? t.next : t.skip} <SkipForward className="w-4 h-4" /></button>
                {savedRepos.length > 0 && <div className="text-xs text-white/40 self-center ml-2 font-mono">// {savedRepos.length} {lang === "pt" ? "conectado(s)" : "connected"}</div>}
              </div>
            </div>
          </>
        )}

        {/* STEP 5 — done */}
        {step === 5 && (
          <div className="surface p-12 text-center">
            <div className="w-14 h-14 bg-primary/20 mx-auto flex items-center justify-center mb-5">
              <Check className="w-7 h-7 text-primary" strokeWidth={2.5} />
            </div>
            <h1 className="font-display font-black text-4xl tracking-tighter mb-3">{t.s5Title}</h1>
            <p className="text-white/60 mb-2 text-sm">{t.s5Desc}</p>
            <div className="my-6 text-xs text-white/40 font-mono">
              boards: {savedBoards.length} · logs: {savedLogs.length} · dbs: {savedDbs.length} · repos: {savedRepos.length}
            </div>
            <button onClick={finish} className="btn-primary" data-testid="onb-finish-btn">{t.s5Open} <ArrowRight className="w-4 h-4" /></button>
          </div>
        )}
      </div>
    </div>
  );
}
